import React, { useState } from 'react';
import { 
  ChartBarIcon, 
  BoltIcon, 
  Cog6ToothIcon, 
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const AIAnalytics = () => {
  const [selectedMetric, setSelectedMetric] = useState<string>('all');
  const [selectedChartType, setSelectedChartType] = useState<string>('line');
  const [timeRange, setTimeRange] = useState<string>('week');
  const [selectedInsight, setSelectedInsight] = useState<string>('behavior');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [selectedTool, setSelectedTool] = useState<string>('all');
  const [showToolDetails, setShowToolDetails] = useState<boolean>(false);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">AI Analytics Dashboard</h1>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Platform Performance Metrics</h2>
          <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors">
            Generate Report
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">User Engagement</h3>
            <div className="text-2xl font-bold text-[#FF5722]">85%</div>
            <p className="text-sm text-gray-600">+5% vs last month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">System Performance</h3>
            <div className="text-2xl font-bold text-[#FF5722]">98.5%</div>
            <p className="text-sm text-gray-600">Uptime this month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">AI Predictions</h3>
            <div className="text-2xl font-bold text-[#FF5722]">92%</div>
            <p className="text-sm text-gray-600">Accuracy rate</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Response Time</h3>
            <div className="text-2xl font-bold text-[#FF5722]">245ms</div>
            <p className="text-sm text-gray-600">Average</p>
          </div>
        </div>
      </div>

      {/* Performance Metrics Visualization */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-4 items-center">
            {/* Metric Selection */}
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
            >
              <option value="all">All Metrics</option>
              <option value="engagement">User Engagement</option>
              <option value="performance">System Performance</option>
              <option value="predictions">AI Predictions</option>
              <option value="response">Response Time</option>
            </select>

            {/* Chart Type Selection */}
            <select
              value={selectedChartType}
              onChange={(e) => setSelectedChartType(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
            >
              <option value="line">Line Chart</option>
              <option value="bar">Bar Chart</option>
              <option value="area">Area Chart</option>
              <option value="scatter">Scatter Plot</option>
            </select>

            {/* Time Range Selection */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
            >
              <option value="day">Last 24 Hours</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
          </div>

          {/* Additional Controls */}
          <div className="flex space-x-2">
            <button 
              className="p-2 text-[#FF5722] hover:text-[#F4511E] rounded-md"
              onClick={() => console.log('Refreshing data...')}
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
            <button 
              className="p-2 text-[#FF5722] hover:text-[#F4511E] rounded-md"
              onClick={() => console.log('Downloading chart...')}
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Chart Area */}
        <div className="mt-4 bg-white rounded-lg p-6 border border-gray-200">
          <div className="h-80 flex items-center justify-center">
            {selectedMetric === 'all' ? (
              <div className="grid grid-cols-2 gap-4 w-full h-full">
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                  <span className="text-gray-500">User Engagement {selectedChartType} Chart</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                  <span className="text-gray-500">System Performance {selectedChartType} Chart</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                  <span className="text-gray-500">AI Predictions {selectedChartType} Chart</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center">
                  <span className="text-gray-500">Response Time {selectedChartType} Chart</span>
                </div>
              </div>
            ) : (
              <div className="w-full h-full bg-gray-50 rounded-lg flex items-center justify-center">
                <span className="text-gray-500">
                  {selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)} {selectedChartType} Chart
                </span>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-4 flex justify-center space-x-6">
            <span className="flex items-center text-sm text-gray-600">
              <span className="w-3 h-3 bg-[#FF5722] rounded-full mr-2"></span>
              Current Period
            </span>
            <span className="flex items-center text-sm text-gray-600">
              <span className="w-3 h-3 bg-gray-300 rounded-full mr-2"></span>
              Previous Period
            </span>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">AI-Powered Insights</h2>
          <div className="flex space-x-4">
            <select
              value={selectedInsight}
              onChange={(e) => setSelectedInsight(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
            >
              <option value="behavior">User Behavior</option>
              <option value="performance">Performance</option>
              <option value="predictive">Predictive</option>
            </select>
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="text-[#FF5722] hover:text-[#F4511E] font-medium"
            >
              {showDetails ? 'Show Less' : 'Show Details'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* User Behavior Analysis */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">User Behavior Analysis</h3>
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Real-time
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Peak Hours</span>
                  <span className="text-sm text-[#FF5722]">6PM - 9PM</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '75%' }} />
                </div>
              </div>

              {showDetails && (
                <>
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium mb-2">Key Findings</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        Highest user activity: Evening hours
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        Most active regions: North America
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        Popular features: SMS scheduling
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-md p-3">
                    <h4 className="text-sm font-medium text-[#FF5722] mb-2">Recommendations</h4>
                    <p className="text-sm text-gray-600">
                      Schedule maintenance during 2AM-4AM when activity is lowest
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Performance Optimization */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <BoltIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">Performance Optimization</h3>
              </div>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                Optimized
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Query Performance</span>
                  <span className="text-sm text-green-600">+15%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-green-500 rounded-full" style={{ width: '85%' }} />
                </div>
              </div>

              {showDetails && (
                <>
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium mb-2">Improvements</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                        Query optimization: 0.3s faster
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                        Cache hit rate: 95%
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                        Resource utilization: -20%
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-md p-3">
                    <h4 className="text-sm font-medium text-[#FF5722] mb-2">Next Steps</h4>
                    <p className="text-sm text-gray-600">
                      Implement query caching for most frequent API calls
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Predictive Analytics */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">Predictive Analytics</h3>
              </div>
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                Forecast
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Growth Trend</span>
                  <span className="text-sm text-[#FF5722]">+20%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '90%' }} />
                </div>
              </div>

              {showDetails && (
                <>
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium mb-2">Predictions</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        User growth: +20% next month
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        Resource needs: +30% capacity
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#FF5722] rounded-full mr-2" />
                        Peak load: 2x current
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-md p-3">
                    <h4 className="text-sm font-medium text-[#FF5722] mb-2">Action Items</h4>
                    <p className="text-sm text-gray-600">
                      Scale server resources by 30% within 2 weeks
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Tools Integration */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">AI Tools Status</h2>
          <div className="flex space-x-4">
            <select
              value={selectedTool}
              onChange={(e) => setSelectedTool(e.target.value)}
              className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
            >
              <option value="all">All Tools</option>
              <option value="mixpanel">Mixpanel Analytics</option>
              <option value="newrelic">New Relic AI</option>
              <option value="sentry">Sentry Error Tracking</option>
            </select>
            <button
              onClick={() => console.log('Refreshing tool status...')}
              className="p-2 text-[#FF5722] hover:text-[#F4511E] rounded-md"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Mixpanel Analytics Card */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">Mixpanel Analytics</h3>
              </div>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Performance</span>
                  <span className="text-sm font-medium">98%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '98%' }} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Events Tracked</span>
                  <p className="text-lg font-medium">1.2M</p>
                </div>
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Active Users</span>
                  <p className="text-lg font-medium">45.3K</p>
                </div>
              </div>

              <button 
                onClick={() => setShowToolDetails(true)}
                className="w-full text-[#FF5722] text-sm hover:text-[#F4511E] mt-2"
              >
                View Analytics Dashboard
              </button>
            </div>
          </div>

          {/* New Relic AI Card */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <BoltIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">New Relic AI</h3>
              </div>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Performance</span>
                  <span className="text-sm font-medium">95%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '95%' }} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Alerts Today</span>
                  <p className="text-lg font-medium">23</p>
                </div>
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Response Time</span>
                  <p className="text-lg font-medium">142ms</p>
                </div>
              </div>

              <button 
                onClick={() => setShowToolDetails(true)}
                className="w-full text-[#FF5722] text-sm hover:text-[#F4511E] mt-2"
              >
                View Performance Metrics
              </button>
            </div>
          </div>

          {/* Sentry Error Tracking Card */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Cog6ToothIcon className="h-6 w-6 text-[#FF5722] mr-2" />
                <h3 className="font-medium">Sentry Error Tracking</h3>
              </div>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Active
              </span>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Performance</span>
                  <span className="text-sm font-medium">99%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '99%' }} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Issues Today</span>
                  <p className="text-lg font-medium">7</p>
                </div>
                <div className="bg-white rounded-md p-3">
                  <span className="text-xs text-gray-500">Resolved</span>
                  <p className="text-lg font-medium">5</p>
                </div>
              </div>

              <button 
                onClick={() => setShowToolDetails(true)}
                className="w-full text-[#FF5722] text-sm hover:text-[#F4511E] mt-2"
              >
                View Error Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Tool Details Window */}
        {showToolDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium">Tool Details</h3>
                <button 
                  onClick={() => setShowToolDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="h-96 flex items-center justify-center">
                  <span className="text-gray-500">Tool-specific dashboard will be displayed here</span>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowToolDetails(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalytics; 